import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Progress } from "components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  ArrowLeft,
  Clock,
  Users,
  DollarSign,
  Building2,
  MapPin,
  Globe,
  FileText,
  AlertTriangle,
  Heart,
} from "lucide-react";
import { ZoomableImage } from "components/common/ZoomableImage";
import { getCampaignImageUrls } from "lib/campaignGallery";
import { getCampaignFinancialStats } from "lib/projects";
import { useCampaignProject } from "hooks/useCampaignProject";
import { OPPORTUNITIES_PATH } from "lib/opportunitiesNav";
import { cn } from "lib/utils";
import swtAlert from "lib/sweetAlert";
import { selectIsAuthenticated } from "store/reducer";
import AppCarousel from "components/common/AppCarousel";
import { AppShareButton } from "components/common/AppShareButton";
import { NewsletterSignupSection } from "components/cta-section";
import { FaqPreviewSection } from "components/faq-preview-section";
import { HowItWorksSection } from "components/how-it-works-section";
import { CommonInvestButton } from "investment/CommonInvestButton";

const projectHtmlClass =
  "project-detail-html max-w-none text-sm leading-relaxed text-foreground [&_p]:mb-4 [&_p:last-child]:mb-0 [&_h3]:mb-3 [&_h3]:mt-8 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:first:mt-0 [&_table]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-2 [&_th]:border [&_th]:border-border [&_th]:px-3 [&_th]:py-2 [&_a]:text-accent [&_a]:underline-offset-2 hover:[&_a]:underline";

function TabSectionTitle({ children }) {
  return (
    <div className="mb-6">
      <h2 className="text-2xl font-semibold tracking-tight text-foreground">{children}</h2>
      <div className="mt-3 h-0.5 w-full bg-accent" aria-hidden />
    </div>
  );
}

TabSectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
};

function getCampaignUpdateKey(update) {
  const date = String(update?.date ?? "").trim();
  const title = String(update?.updateTitle ?? update?.title ?? "").trim();
  if (!date && !title) return "campaign-update";
  return `${date}-${title}`;
}

function CampaignDetailLoading() {
  return (
    <div className="container mx-auto px-4 py-16 text-center text-muted-foreground">
      Loading project…
    </div>
  );
}

function CampaignDetailError({ message }) {
  return (
    <div className="container mx-auto space-y-4 px-4 py-16 text-center">
      <p className="text-destructive">{message}</p>
      <Button
        variant="outline"
        asChild
        className="d-none"
      >
        <Link to={OPPORTUNITIES_PATH}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to opportunities
        </Link>
      </Button>
    </div>
  );
}

CampaignDetailError.propTypes = {
  message: PropTypes.string.isRequired,
};

const detailTabListClass =
  "flex h-auto min-h-11 w-full flex-wrap items-end justify-start gap-0 rounded-none border-0 border-b border-border bg-transparent p-0 shadow-none";
const detailTabTriggerClass =
  "relative rounded-none border border-transparent border-b-0 px-4 py-2.5 text-sm font-medium text-accent shadow-none transition-colors data-[state=inactive]:hover:text-accent/80 data-[state=active]:z-[1] data-[state=active]:-mb-px data-[state=active]:rounded-t-md data-[state=active]:border-border data-[state=active]:border-b-card data-[state=active]:bg-card data-[state=active]:text-foreground dark:data-[state=active]:border-border dark:data-[state=active]:border-b-card";
const detailTabContentClass =
  "rounded-b-lg rounded-tr-lg border border-border bg-card p-6 sm:p-8 shadow-none dark:bg-card";

function investmentPresets(campaign) {
  if (!campaign) return [100, 500, 1000, 5000];
  const min = Number(campaign.minInvestment) || 0;
  const max = Number(campaign.maxInvestment) || min;
  if (min <= 0 && max <= 0) return [100, 500, 1000, 5000];
  const lo = Math.max(min, 1);
  const hi = Math.max(max, lo);
  const mid = Math.round((lo + hi) / 2);
  const second = Math.min(hi, Math.round(lo * 2));
  return [...new Set([lo, second, mid, hi])].filter((v) => v >= lo && v <= hi).slice(0, 4);
}

function CampaignDetailTabs({ campaign }) {
  return (
    <Tabs defaultValue="project-details" className="w-full gap-0">
      <TabsList className={detailTabListClass}>
        <TabsTrigger value="project-details" className={detailTabTriggerClass}>
          Project Details
        </TabsTrigger>
        <TabsTrigger value="company-details" className={detailTabTriggerClass}>
          Company Details
        </TabsTrigger>
        <TabsTrigger value="whos-involved" className={detailTabTriggerClass}>
          Who is Involved
        </TabsTrigger>
        <TabsTrigger value="offering-document" className={detailTabTriggerClass}>
          Offering Document
        </TabsTrigger>
        <TabsTrigger value="news" className={detailTabTriggerClass}>
          News & Updates
        </TabsTrigger>
      </TabsList>

      <TabsContent value="project-details" className={cn(detailTabContentClass, "mt-0")}>
        <TabSectionTitle>Overview</TabSectionTitle>
        {campaign.longDescriptionHtml ? (
          <div
            className={projectHtmlClass}
            dangerouslySetInnerHTML={{ __html: campaign.longDescriptionHtml }}
          />
        ) : (
          <p className="text-sm text-muted-foreground">No project description available.</p>
        )}
      </TabsContent>

      <TabsContent value="company-details" className={cn(detailTabContentClass, "mt-0")}>
        <TabSectionTitle>{campaign.title} (the &ldquo;Issuer&rdquo;)</TabSectionTitle>
        <div className="space-y-4 text-sm">
          <div>
            <span className="font-semibold text-foreground">Company website: </span>
            {campaign.company.website ? (
              <a
                href={campaign.company.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline-offset-2 hover:underline"
              >
                {campaign.company.website}
              </a>
            ) : (
              <span className="text-muted-foreground">N/A</span>
            )}
          </div>
          <div>
            <div className="font-semibold text-foreground">Head office address</div>
            <p className="mt-1 text-muted-foreground">
              {campaign.company.location && campaign.company.location !== "—"
                ? campaign.company.location
                : "N/A"}
            </p>
          </div>
          <div>
            <div className="font-semibold text-foreground">Company phone no</div>
            <p className="mt-1 text-muted-foreground">{campaign.companyPhone || "N/A"}</p>
          </div>
          <div>
            <div className="font-semibold text-foreground">Company email address</div>
            <p className="mt-1">
              {campaign.companyEmail ? (
                <a
                  href={`mailto:${campaign.companyEmail}`}
                  className="text-accent underline-offset-2 hover:underline"
                >
                  {campaign.companyEmail}
                </a>
              ) : (
                <span className="text-muted-foreground">N/A</span>
              )}
            </p>
          </div>
        </div>
        {campaign.companyOverviewHtml ? (
          <div
            className={cn(projectHtmlClass, "mt-8")}
            dangerouslySetInnerHTML={{ __html: campaign.companyOverviewHtml }}
          />
        ) : null}
      </TabsContent>

      <TabsContent value="whos-involved" className={cn(detailTabContentClass, "mt-0")}>
        <TabSectionTitle>All Team Members &amp; Relevant Parties</TabSectionTitle>
        {campaign.team.length === 0 ? (
          <p className="text-sm text-muted-foreground">No team members listed.</p>
        ) : (
          <div className="space-y-0">
            {campaign.team.map((member, index) => (
              <div
                key={`${member.name}-${index}`}
                className={cn(index > 0 && "mt-8 border-t-2 border-accent pt-8")}
              >
                <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                  <div className="shrink-0 sm:w-40">
                    {member.image ? (
                      <ZoomableImage
                        src={member.image}
                        alt={member.name}
                        className="rounded-md border border-border object-cover"
                        containerClassName="h-36 w-36 sm:h-40 sm:w-40"
                      />
                    ) : (
                      <div className="flex h-36 w-36 items-center justify-center rounded-md border border-dashed border-border bg-muted text-xs text-muted-foreground sm:h-40 sm:w-40">
                        No photo
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 space-y-4 text-sm">
                    <h3 className="text-lg font-semibold text-foreground">{member.name}</h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="font-semibold text-foreground">Position at Issuer</dt>
                        <dd className="mt-1 text-muted-foreground">{member.role || "—"}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-foreground">Municipality of Residence</dt>
                        <dd className="mt-1 text-muted-foreground">
                          {member.municipality || "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-foreground">Email Address</dt>
                        <dd className="mt-1">
                          {member.email ? (
                            <a
                              href={`mailto:${member.email}`}
                              className="text-accent underline-offset-2 hover:underline"
                            >
                              {member.email}
                            </a>
                          ) : (
                            <span className="text-muted-foreground">N/A</span>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-foreground">
                          Principal Occupation for the Last Five Years
                        </dt>
                        <dd className="mt-1 text-muted-foreground whitespace-pre-wrap">
                          {member.principalOccupation || "—"}
                        </dd>
                      </div>
                    </dl>
                    {member.aboutHtml ? (
                      <div>
                        <p className="mb-2 font-semibold text-foreground">About:</p>
                        <div
                          className={projectHtmlClass}
                          dangerouslySetInnerHTML={{ __html: member.aboutHtml }}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </TabsContent>

      <TabsContent value="offering-document" className={cn(detailTabContentClass, "mt-0")}>
        <TabSectionTitle>Offering Documents</TabSectionTitle>
        {campaign.documents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No offering documents uploaded.</p>
        ) : (
          <div className="space-y-3">
            {campaign.documents.map((doc, index) => (
              <a
                key={`${doc.name}-${index}`}
                href={doc.url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Card className="transition-colors hover:bg-muted/40">
                  <CardContent className="flex items-center gap-3 py-4">
                    <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">{doc.name}</span>
                    </div>
                    <Badge variant="outline">{doc.type}</Badge>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        )}
      </TabsContent>

      <TabsContent value="news" className={cn(detailTabContentClass, "mt-0")}>
        <TabSectionTitle>News &amp; Updates</TabSectionTitle>
        {campaign.newsUpdates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {campaign.title} doesn&apos;t have any updates yet. Stay tuned!
          </p>
        ) : (
          <div className="space-y-4">
            {campaign.newsUpdates.map((update) => (
              <Card key={getCampaignUpdateKey(update)}>
                <CardContent className="pt-6">
                  <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
                    <span>
                      {update.date
                        ? new Date(update.date).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : "—"}
                    </span>
                  </div>
                  <h4 className="mb-2 font-semibold">{update.updateTitle}</h4>
                  {update.updateDescription ? (
                    <div
                      className={cn(projectHtmlClass, "text-muted-foreground")}
                      dangerouslySetInnerHTML={{ __html: update.updateDescription }}
                    />
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}

CampaignDetailTabs.propTypes = {
  campaign: PropTypes.object.isRequired,
};

function SidebarStat({ label, value }) {
  return (
    <div className="flex min-w-0 items-start justify-between gap-3">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 wrap-break-word text-right font-medium tabular-nums">
        {value}
      </span>
    </div>
  );
}

SidebarStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

function CampaignInvestSidebar({
  campaign,
  investmentAmount,
  onInvestmentAmountChange,
  formatCurrency,
  percentFunded,
  financials,
  sharesForInvestment,
  presets,
}) {
  return (
    <div className="sticky top-24">
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-6">
            <div>
              <div className="mb-2 flex items-baseline justify-between">
                <span className="text-2xl font-bold">{formatCurrency(campaign.raised)}</span>
                <span className="text-sm text-muted-foreground">
                  of {formatCurrency(campaign.goal)}
                </span>
              </div>
              <Progress value={percentFunded} className="h-3" />
              <p className="mt-2 text-sm text-muted-foreground">
                {percentFunded.toFixed(0)}% funded
              </p>
            </div>

            <div className="space-y-3 border-t border-border pt-4 text-sm">
              <SidebarStat label="Funds raised" value={formatCurrency(financials.fundsRaised)} />
              <SidebarStat label="Hard cap" value={formatCurrency(financials.hardCap)} />
              <SidebarStat
                label="Total funds required"
                value={formatCurrency(financials.totalFundsRequired)}
              />
              <SidebarStat label="Investor count" value={financials.investorCount} />
              <SidebarStat
                label="Minimum investment"
                value={formatCurrency(financials.minimumInvestmentAmount)}
              />
              <SidebarStat
                label="Maximum investment"
                value={formatCurrency(financials.maximumInvestmentAmount)}
              />
              <SidebarStat
                label="Share price"
                value={`$${financials.sharePriceAmount.toFixed(2)}`}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg bg-muted p-3 text-center">
                <Users className="mx-auto mb-1 h-5 w-5 text-muted-foreground" />
                <div className="font-semibold">{financials.investorCount}</div>
                <div className="text-xs text-muted-foreground">Investors</div>
              </div>
              <div className="rounded-lg bg-muted p-3 text-center">
                <Clock className="mx-auto mb-1 h-5 w-5 text-muted-foreground" />
                <div className="font-semibold">{campaign.daysRemaining}</div>
                <div className="text-xs text-muted-foreground">Days left</div>
              </div>
            </div>

            <div className="space-y-3">
              <Label htmlFor="amount">Investment amount</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="amount"
                  type="number"
                  min={campaign.minInvestment}
                  max={campaign.maxInvestment}
                  value={investmentAmount}
                  onChange={(e) => onInvestmentAmountChange(Number(e.target.value))}
                  className="pl-10"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {presets.map((amount) => (
                  <Button
                    key={amount}
                    variant="outline"
                    size="sm"
                    className="flex-1 min-w-16"
                    type="button"
                    onClick={() => onInvestmentAmountChange(amount)}
                  >
                    {formatCurrency(amount)}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Min: {formatCurrency(campaign.minInvestment)} · Max:{" "}
                {formatCurrency(campaign.maxInvestment)}
              </p>
            </div>

            <div className="rounded-lg bg-muted p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shares</span>
                <span>{sharesForInvestment.toLocaleString()} shares</span>
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-muted-foreground">Price per share</span>
                <span>${financials.sharePriceAmount.toFixed(2)}</span>
              </div>
            </div>

            <CommonInvestButton
              variant="navigate"
              projectId={campaign.id}
              amount={investmentAmount}
              minInvestment={campaign.minInvestment}
              maxInvestment={campaign.maxInvestment}
              campaignStartAt={campaign.campaignStartAt}
              campaignEndAt={campaign.campaignEndAt}
              daysRemaining={campaign.daysRemaining}
              status={campaign.status}
              canInvest={campaign.canInvest}
              isSample={campaign.isSample}
            />

            <p className="text-center text-xs text-muted-foreground">
              By investing, you agree to our <Link to="/terms" className="font-medium text-accent hover:underline">Terms of Use</Link> and acknowledge the investment risks.
              <Link to="/terms" className="font-medium text-accent hover:underline">
                Terms of Use
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="font-medium text-accent hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

CampaignInvestSidebar.propTypes = {
  campaign: PropTypes.object.isRequired,
  investmentAmount: PropTypes.number.isRequired,
  onInvestmentAmountChange: PropTypes.func.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  percentFunded: PropTypes.number.isRequired,
  financials: PropTypes.object.isRequired,
  sharesForInvestment: PropTypes.number.isRequired,
  presets: PropTypes.arrayOf(PropTypes.number).isRequired,
};

export default function CampaignDetailPage() {
  const { id = "" } = useParams();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { campaign, loading, error } = useCampaignProject(id);
  const [investmentAmount, setInvestmentAmount] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);

  const handleWishlistToggle = useCallback(() => {
    if (!isAuthenticated) {
      swtAlert.warning(
        "Sign in required",
        "Please sign in to save campaigns to your wishlist."
      );
      return;
    }
    setIsWishlisted((prev) => !prev);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!campaign) return;
    const min = Number(campaign.minInvestment) || 0;
    const max = Number(campaign.maxInvestment) || min;
    const start = min > 0 ? min : Math.min(100, max) || 100;
    setInvestmentAmount(Math.min(Math.max(start, min || 0), max || start));
  }, [campaign]);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  }, []);

  const percentFunded = useMemo(() => {
    if (!campaign) return 0;
    const g = campaign.goal > 0 ? campaign.goal : 1;
    return Math.min((campaign.raised / g) * 100, 100);
  }, [campaign]);

  const sharesForInvestment = useMemo(() => {
    if (!campaign) return 0;
    const sp = Number(campaign.sharePrice) || 1;
    return Math.floor(Number(investmentAmount) / sp);
  }, [campaign, investmentAmount]);

  const presets = useMemo(() => investmentPresets(campaign), [campaign]);

  const financials = useMemo(
    () => getCampaignFinancialStats(campaign),
    [campaign]
  );

  const carouselItems = useMemo(() => {
    if (!campaign) return [];
    return getCampaignImageUrls(campaign).map((src, i) => ({
      id: `${campaign.id}-${i}`,
      src,
      alt: `${campaign.title} – image ${i + 1}`,
    }));
  }, [campaign]);

  if (loading) {
    return <CampaignDetailLoading />;
  }

  if (error || !campaign) {
    return <CampaignDetailError message={error || "Project not found"} />;
  }

  return (
    <>
      <Helmet>
        <title>{campaign.title} | Fortify</title>
      </Helmet>

      <section className="border-b bg-muted/20 py-6 sm:py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          {carouselItems.length > 0 ? (
            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
              <AppCarousel
                key={campaign.id}
                items={carouselItems}
                heightClassName="min-h-[220px] h-[min(40vw,480px)] w-full max-h-[520px] md:h-[min(38vw,520px)]"
              />
            </div>
          ) : (
            <div className="flex aspect-3/1 w-full max-h-[520px] items-center justify-center overflow-hidden rounded-xl border border-border bg-muted text-muted-foreground">
              <Building2 className="h-16 w-16 opacity-40" aria-hidden />
            </div>
          )}
        </div>
      </section>

      <section className="py-8 sm:py-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="grid gap-8 lg:grid-cols-3">
            <div className="space-y-8 lg:col-span-2">
              <div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <Badge variant="secondary">{campaign.category}</Badge>
                      {campaign.isSample ? (
                        <Badge variant="destructive">Sample Project</Badge>
                      ) : null}
                    </div>
                    <h1 className="text-2xl font-bold sm:text-3xl">{campaign.title}</h1>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      type="button"
                      aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                      onClick={handleWishlistToggle}
                    >
                      <Heart
                        className={`h-4 w-4 ${isWishlisted ? "fill-destructive text-destructive" : ""}`}
                      />
                    </Button>
                    <AppShareButton
                      title={campaign.title}
                      text={campaign.description}
                      ariaLabel="Share campaign"
                    />
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Building2 className="h-4 w-4 shrink-0" />
                    <span>
                      Owner: {campaign.issuerName || campaign.company.name}
                    </span>
                  </div>
                  {campaign.company.location && campaign.company.location !== "—" ? (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-4 w-4" />
                      <span>{campaign.company.location}</span>
                    </div>
                  ) : null}
                  {campaign.company.website ? (
                    <div className="flex items-center gap-1.5">
                      <Globe className="h-4 w-4" />
                      <a
                        href={campaign.company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-foreground"
                      >
                        Website
                      </a>
                    </div>
                  ) : null}
                </div>
              </div>

              <CampaignDetailTabs campaign={campaign} />

              <div className="mt-10 rounded-lg border border-warning/50 bg-warning/5 p-4 text-sm">
                <div className="flex gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
                  <div className="space-y-2">
                    <p className="font-medium text-foreground">Investment risk</p>
                    <p className="text-muted-foreground">
                      Crowdfunding investments are high risk and may be illiquid. Read our full disclosure
                      before investing.
                    </p>
                    <Link to="/investor-overview" className="inline-block text-accent underline-offset-2 hover:underline">
                      Read full risk disclosure →
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <CampaignInvestSidebar
                campaign={campaign}
                investmentAmount={investmentAmount}
                onInvestmentAmountChange={setInvestmentAmount}
                formatCurrency={formatCurrency}
                percentFunded={percentFunded}
                financials={financials}
                sharesForInvestment={sharesForInvestment}
                presets={presets}
              />
            </div>
          </div>
        </div>
      </section>

      <HowItWorksSection />
      <NewsletterSignupSection showAuthenticatedLinks={false} />
      <FaqPreviewSection />
    </>
  );
}
